import _ from 'lodash'
import { useTranslation } from 'react-i18next'

import { Button, Stack, TextField, Typography, styled } from '@mui/material'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'

import { DeviceProps } from '@/components/ui/dialogs/DeviceSelectionDialog'
import AudioViewer from '@/components/ui/preview/AudioViewer'

import { Dictaphone } from '@/pages/diagnostic/components/Dictaphone'

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />} {...props} />
))(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, .05)' : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}))

type TranscriptionsProps = {
  devices: DeviceProps[]
  dictaphones: Array<{
    filename: string
    key: string
    src: string
    text: string
  }>
  isUploading?: boolean
  onDeleteDictaphone?: (filename: string, key: string) => void
  onRecordingStopped?: (file: File) => void
}

export function Transcriptions(props: TranscriptionsProps) {
  const { devices, dictaphones, isUploading = false, onDeleteDictaphone, onRecordingStopped } = props
  const { t } = useTranslation()

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h3" component="div" noWrap sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
          {t('page.expertise.transcriptions.title')}
        </Typography>
        <Dictaphone devices={devices} />
      </Stack>
      <div>
        {dictaphones.map((dictaphone, index) => (
          <Accordion key={index}>
            <AccordionSummary sx={{ display: 'flex' }}>
              <Typography>{_.truncate(dictaphone.text, { length: 20 })}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 2 }}>
              <Stack key={dictaphone.key} spacing={2}>
                <AudioViewer src={dictaphone.src} sx={{ width: '100%' }} />
                <TextField fullWidth InputProps={{ readOnly: true }} minRows={4} multiline={true} defaultValue={dictaphone.text} />
                <Stack direction="row" justifyContent="end">
                  <Button
                    color="error"
                    onClick={() => {
                      onDeleteDictaphone?.(dictaphone.filename, dictaphone.key)
                    }}
                    startIcon={<DeleteRoundedIcon />}
                    variant="contained"
                  >
                    {t('common:button.delete.label')}
                  </Button>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    </Stack>
  )
}

export default Transcriptions
